import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    useTheme,
    Button,
    Stack,
    Skeleton,
    Drawer,
    CardActionArea,
    IconButton,
    Card,
} from '@mui/material';
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from "apexcharts";
import moment from 'moment';
import axiosInstance from '../../../axios/axios';
import { END_POINTS } from '../../../shared';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Security as SecurityIcon } from "@mui/icons-material";
import { Timeline as TimelineIcon } from "@mui/icons-material";
import { Event as EventIcon } from "@mui/icons-material";
import { TrendingUp as TrendingUpIcon } from "@mui/icons-material";
import { TrendingDown as TrendingDownIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Layer123 from "../../../assets/Layer123.svg";
import { CyberResilienceRediretions } from '../../../modules/dashboard/cyber_resilience/CyberResilienceRediretions';

type ChangeBreakdown = {
    all?: number;
    additions?: number;
    deletions?: number;
    modified?: number;
};

type TrendGraphPoint = {
    scan_id?: string;
    timestamp?: string;
    number_of_changes?: number;
    posture_score?: number;
    assets_scanned?: number;
    change_breakdown?: ChangeBreakdown;
    has_scan?: boolean;
};

type TrendGraphPayload = {
    scans?: TrendGraphPoint[];
    total_changes?: number;
    total_posture_score?: number;
};

type TrendsData = {
    hours: string[];
    timestamps: string[]; // Full timestamps for tooltip display
    postureScores: number[];
    numberOfChanges: number[];
    newEvents: boolean[]; // Track which points have new events
    latestPostureScore: number;
    latestNumChanges: number;
};

type TrendsCardProps = {
    height?: number;
    compact?: boolean;
    trendGraphData?: any;
    deviceId?: string;
};

interface ChartDataPoint {
    x: number;
    y: number;
    new_event: boolean;
    date_time: string;
}

const formatTimeAgo = (dateTime: string): string => {
    try {
        return dayjs(dateTime).format("HH:mm:ss");
    } catch {
        return `**${dayjs(dateTime).format("HH:mm:ss")}**`;
    }
};

const roundValue = (value?: number): number => {
    if (typeof value !== "number" || isNaN(value)) return 0;
    return Math.round(value);
};

const EventsTrendsCard: React.FC<TrendsCardProps> = ({ height, compact = false, trendGraphData, deviceId }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const [isExpanded, setIsExpanded] = useState(true);
    const [timeRange, setTimeRange] = useState('30days');
    const [fetchedTrendData, setFetchedTrendData] = useState<any>(null);
    const [isChartLoading, setIsChartLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [dateRangeError, setDateRangeError] = useState<string>('');
    const [selectOpen, setSelectOpen] = useState(false);
    const [customDays, setCustomDays] = useState<number | null>(null);
    const [appliedFromDate, setAppliedFromDate] = useState<Dayjs | null>(null);
    const [appliedToDate, setAppliedToDate] = useState<Dayjs | null>(null);
    const navigate = useNavigate();

    const normaliseTrendGraphData = (payload?: TrendGraphPayload, currentTimeRange?: string): TrendsData => {
        // If there's no payload or no scans, return empty data (no mock/random values)
        if (!payload || !Array.isArray(payload.scans) || payload.scans.length === 0) {
            return {
                hours: [],
                timestamps: [],
                postureScores: [],
                numberOfChanges: [],
                newEvents: [],
                latestPostureScore: 0,
                latestNumChanges: 0,
            };
        }

        const timestampFormats = ['DD MMM YYYY HH:mm:ss', moment.ISO_8601];

        const sortedData = [...payload.scans].sort((a, b) => {
            const first = moment(a?.timestamp, timestampFormats, true);
            const second = moment(b?.timestamp, timestampFormats, true);
            const firstValue = first.isValid() ? first.valueOf() : 0;
            const secondValue = second.isValid() ? second.valueOf() : 0;
            return firstValue - secondValue;
        });

        let daysInRange = 30; // Default
        if (currentTimeRange === '7days') {
            daysInRange = 7;
        } else if (currentTimeRange === '30days') {
            daysInRange = 30;
        } else if (currentTimeRange === '90days') {
            daysInRange = 90;
        } else if (currentTimeRange === '180days') {
            daysInRange = 180;
        } else if (currentTimeRange === 'custom' && sortedData.length >= 2) {
            // For custom range, calculate days from the actual data
            const firstDate = moment(sortedData[0]?.timestamp, timestampFormats, true);
            const lastDate = moment(sortedData[sortedData.length - 1]?.timestamp, timestampFormats, true);
            if (firstDate.isValid() && lastDate.isValid()) {
                daysInRange = lastDate.diff(firstDate, 'days') + 1;
            }
        }

        // Format x-axis labels based on time range
        let hours: string[];

        if (daysInRange <= 15) {
            // For 7 days: show every day (e.g., "12 Dec", "13 Dec", etc.)
            hours = sortedData.map((point) => {
                const parsed = moment(point.timestamp, timestampFormats, true);
                return parsed.isValid() ? parsed.format("DD MMM") : point.timestamp ?? '—';
            });
        } else if (daysInRange <= 31) {
            // For 1 month: show month-year labels without duplicates (matching TrendsCard)
            const seenMonths = new Set<string>();
            hours = sortedData.map((point) => {
                const parsed = moment(point.timestamp, timestampFormats, true);
                if (!parsed.isValid()) return point.timestamp ?? '—';

                // Use YYYY-MM as the unique key to ensure each month appears only once
                const monthKey = parsed.format('YYYY-MM'); // Year and month
                const dateLabel = parsed.format("MMM 'YY");

                // Show first occurrence of each month
                if (seenMonths.has(monthKey)) {
                    return ''; // Return empty string for same month
                }
                seenMonths.add(monthKey);
                return dateLabel;
            });
        } else {
            // For longer periods (90 days, 180 days, custom > 31 days): show all unique months
            const seenMonths = new Set<string>();

            hours = sortedData.map((point) => {
                const parsed = moment(point.timestamp, timestampFormats, true);
                const monthLabel = parsed.isValid() ? parsed.format("MMM 'YY") : point.timestamp ?? '—';

                // Only show label for first occurrence of each month
                if (seenMonths.has(monthLabel)) {
                    return ''; // Return empty string for duplicate months
                }
                seenMonths.add(monthLabel);
                return monthLabel;
            });
        }

        const timestamps = sortedData.map((point) => {
            const parsed = moment(point.timestamp, timestampFormats, true);
            // Store full formatted timestamp for tooltip (e.g., "DD MMM YYYY HH:mm:ss")
            return parsed.isValid() ? parsed.format('DD MMM YYYY HH:mm:ss') : point.timestamp ?? '—';
        });

        const postureScores = sortedData.map((point) =>
            typeof point.posture_score === 'number' ? roundValue(point.posture_score) : 0
        );
        const numberOfChanges = sortedData.map((point) =>
            typeof point.number_of_changes === 'number' ? point.number_of_changes : 0
        );

        // For EventsTrendsCard, we'll show markers on all points (since there's no new_event field)
        // Each scan point is considered an event
        const newEvents = sortedData.map(
            (point) => point.has_scan === true
        );

        // Use total values from API response if available
        const latestPostureScore = typeof payload.total_posture_score === 'number'
            ? roundValue(payload.total_posture_score)
            : postureScores[postureScores.length - 1] ?? 0;

        const latestNumChanges = typeof payload.total_changes === 'number'
            ? payload.total_changes
            : numberOfChanges[numberOfChanges.length - 1] ?? 0;

        return {
            hours,
            timestamps,
            postureScores,
            numberOfChanges,
            newEvents,
            latestPostureScore,
            latestNumChanges,
        };
    };

    // Use fetched data if available, otherwise use prop data
    const activeTrendData = useMemo(() => {
        return fetchedTrendData !== null ? fetchedTrendData : trendGraphData;
    }, [fetchedTrendData, trendGraphData]);

    const trendsData = useMemo(
        () => normaliseTrendGraphData(activeTrendData, timeRange),
        [activeTrendData, timeRange]
    );

    // Map timeRange to days
    const getDaysFromTimeRange = (range: string): number => {
        switch (range) {
            case '7days':
                return 7;
            case '30days':
                return 30;
            case '90days':
                return 90;
            case '180days':
                return 180;
            case 'custom':
                return customDays || 7;
            default:
                return 7;
        }
    };

    // Get display text for timeRange
    const getTimeRangeDisplayText = (range: string): string => {
        if (range === 'custom' && appliedFromDate && appliedToDate) {
            const fromDateStr = appliedFromDate.format('DD MMM YYYY');
            const toDateStr = appliedToDate.format('DD MMM YYYY');
            return `${fromDateStr} - ${toDateStr}`;
        }
        switch (range) {
            case '7days':
                return 'Last 7 Days';
            case '30days':
                return 'Last 1 Month';
            case '90days':
                return 'Last 3 Month';
            case '180days':
                return 'Last 6 Month';
            default:
                return 'Last 7 Days';
        }
    };

    const fetchTrendsData = useCallback(async (days: number | null, customStartDate?: string, customEndDate?: string) => {
        if (!deviceId) return;
        setIsChartLoading(true);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        let startDate: string;
        let endDate: string;

        if (customStartDate && customEndDate) {
            startDate = customStartDate;
            endDate = customEndDate;
        } else {
            startDate = moment()
                .subtract(days, "days")
                .format("YYYY-MM-DD");
            endDate = moment().format("YYYY-MM-DD");
        }

        try {
            // Build query params - deviceId is optional
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                timezone: timeZone,
            });
            // if (deviceId) {
            //     params.append('device_id', deviceId);
            // }

            const response = await axiosInstance.get(
                `${END_POINTS.CONFIG_DIFF_EVENTS_TREND_GRAPH}?${params.toString()}`,
            );
            if (response.data.status === 200 || response.status === 200) {
                const graphData = response?.data?.data || null;
                setFetchedTrendData(graphData);
            }
        } catch (error: any) {
            toast.error(error.message);
            setFetchedTrendData(null);
        } finally {
            setIsChartLoading(false);
        }
    }, [deviceId]);

    // Validate date range (minimum 7 days)
    const validateDateRange = (from: Dayjs | null, to: Dayjs | null): string => {
        if (!from || !to) {
            return 'Please select both from and to dates';
        }
        if (from.isAfter(to)) {
            return 'From date cannot be after To date';
        }
        const daysDiff = to.diff(from, 'day');
        if (daysDiff < 7) {
            return 'You can select a minimum period of 7 days';
        }
        return '';
    };

    // Handle apply button click for custom date range
    const handleApplyCustomDateRange = () => {
        const error = validateDateRange(fromDate, toDate);
        if (error) {
            setDateRangeError(error);
            return;
        }
        setDateRangeError('');
        if (deviceId && fromDate && toDate) {
            const startDate = fromDate.format('YYYY-MM-DD');
            const endDate = toDate.format('YYYY-MM-DD');
            // Calculate the number of days
            const days = toDate.diff(fromDate, 'day');
            setCustomDays(days);
            setAppliedFromDate(fromDate);
            setAppliedToDate(toDate);
            setTimeRange('custom');
            fetchTrendsData(null, startDate, endDate);
            setSelectOpen(false); // Close the Select dropdown
        }
    };

    // Reset state and fetch data when deviceId changes
    useEffect(() => {
        if (!deviceId) {
            setFetchedTrendData(null);
            setIsChartLoading(false);
            return;
        }

        // Set loading state FIRST to prevent "No data available" flash
        setIsChartLoading(true);

        // Reset state when device changes
        setFetchedTrendData(null);
        setFromDate(null);
        setToDate(null);
        setDateRangeError('');
        setTimeRange('30days');
        setSelectOpen(false);
        setCustomDays(null);
        setAppliedFromDate(null);
        setAppliedToDate(null);

        // Fetch initial data with default 30 days range (1 month)
        const days = getDaysFromTimeRange('30days');
        fetchTrendsData(days);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    // Fetch data when timeRange changes (only for non-custom ranges)
    // Skip if this is the initial load (timeRange already set to '30days' above)
    const initialLoadRef = React.useRef(true);
    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        if (deviceId && timeRange !== 'custom') {
            const days = getDaysFromTimeRange(timeRange);
            fetchTrendsData(days);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange]);

    // Calculate dynamic max values for y-axes with padding
    const maxPostureScore = useMemo(() => {
        const max = Math.max(...trendsData.postureScores, 0);
        return max > 0 ? Math.ceil(max * 1.1) : 100;
    }, [trendsData.postureScores]);

    const maxNumChanges = useMemo(() => {
        const max = Math.max(...trendsData.numberOfChanges, 0);
        return max > 0 ? Math.ceil(max * 1.1) : 100;
    }, [trendsData.numberOfChanges]);

    const tooltipAbortRefs = useRef<AbortController[]>([]);
    const [activeDataset, setActiveDataset] = useState<string>("all");
    const tooltipDataRef = useRef<any>(null);
    const currentTooltipDateRef = useRef<string | null>(null);
    const tooltipLoadingRef = useRef(false);
    // Abort controller to cancel inflight tooltip requests so only the latest runs
    const tooltipAbortRef = useRef<AbortController | null>(null)
    const [eventListOpen, setEventListOpen] = useState(false);
    const [eventListData, setEventListData] = useState<any>(null);
    const [eventClicked, setEventClicked] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);

    useEffect(() => {
        if (
            onPremData.length === 0 &&
            cloudData.length === 0 &&
            cveData.length === 0 &&
            allData.length === 0
        ) {
            const now = new Date().getTime();
            const initialPoint: ChartDataPoint = {
                x: now,
                y: 0,
                new_event: false,
                date_time: new Date(now).toISOString(),
            };
            setAllData([initialPoint]);
            setOnPremData([initialPoint]);
            setCloudData([initialPoint]);
            setCVEData([initialPoint]);
        }
    }, []);

    const [allData, setAllData] = useState<ChartDataPoint[]>([
        {
            x: new Date().getTime(),
            y: 0,
            new_event: false,
            date_time: new Date().toISOString(),
        },
    ]);
    const [onPremData, setOnPremData] = useState<ChartDataPoint[]>([
        {
            x: new Date().getTime(),
            y: 0,
            new_event: false,
            date_time: new Date().toISOString(),
        },
    ]);
    const [cloudData, setCloudData] = useState<ChartDataPoint[]>([
        {
            x: new Date().getTime(),
            y: 0,
            new_event: false,
            date_time: new Date().toISOString(),
        },
    ]);
    const [cveData, setCVEData] = useState<ChartDataPoint[]>([
        {
            x: new Date().getTime(),
            y: 0,
            new_event: false,
            date_time: new Date().toISOString(),
        },
    ]);

    const handleDatasetChange = (dataset: string) => {
        setActiveDataset(dataset);
        // No need to fetch data again as we already have it
    };

    const fetchTooltipEventData = async (
        date: string,
    ) => {
        // Abort ALL pending requests
        tooltipAbortRefs.current.forEach((ctrl) => {
            try {
                ctrl.abort();
            } catch (_) { }
        });
        tooltipAbortRefs.current = [];

        // If we already have data for this date and dataset, skip fetching
        if (currentTooltipDateRef.current === date && tooltipDataRef.current) {
            return;
        }

        tooltipLoadingRef.current = true;
        currentTooltipDateRef.current = date;

        try {
            const controller = new AbortController();
            tooltipAbortRefs.current.push(controller);

            let url = END_POINTS.CYBERRESILIENCE_DASHBOARD_DAILY;
            let payload: any = {
                date: date,
                timezone: "Asia/Calcutta",
            };

            if (activeDataset === "cve") {
                url = END_POINTS.CYBERRESILIENCE_DASHBOARD_CVE;
                payload.module = "on_premise";
            } else if (activeDataset === "onPrem") {
                url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
                payload.module = "on_premise";
            } else if (activeDataset === "cloud") {
                url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
                payload.module = "cloud";
            }

            const response = await axiosInstance.post(url, payload, {
                signal: controller.signal,
            });

            // Only handle response if it wasn’t aborted
            if (!controller.signal.aborted) {


                const formattedData = {
                    date: dayjs(date).format("DD[th] MMM, YYYY"),
                    data: response.data.data.event_data.map((event: any) => ({
                        time: formatTimeAgo(event.event_date),
                        security_score: parseFloat(event.current_score).toFixed(2),
                        score_change: parseFloat(event.score_difference).toFixed(2),
                        event_type: event.event_name,
                        event_code: event.event_code,
                        referenceID: event.reference_id,
                    })),
                };

                tooltipDataRef.current = formattedData;
            }
        } catch (error) {
            if ((error as any).name !== "CanceledError") {
                console.error("Failed to fetch tooltip event data:", error);
            }
            tooltipDataRef.current = null;
        } finally {
            tooltipLoadingRef.current = false;
        }
    };

    const getMainChartData = () => {
        switch (activeDataset) {
            case "cloud":
                return cloudData;
            case "cve":
                return cveData;
            case "onPrem":
                return onPremData;
            case "all":
            default:
                return allData;
        }
    };

    const memoizedFetchTooltipEventData = useCallback(fetchTooltipEventData, [
        activeDataset,
    ]);

    const handleEventClick = async (date: string) => {
        // Cancel any in-progress tooltip/event list request; latest click should win
        if (tooltipAbortRef.current) {
            try { tooltipAbortRef.current.abort(); } catch (_) { }
            tooltipAbortRef.current = null;
        }
        try {
            const controller = new AbortController();
            tooltipAbortRef.current = controller;
            let url = END_POINTS.CYBERRESILIENCE_DASHBOARD_DAILY;
            let payload: any = {
                date: date,
                timezone: "Asia/Calcutta",
            };
            if (activeDataset === "cve") {
                url = END_POINTS.CYBERRESILIENCE_DASHBOARD_CVE;
                payload.module = "on_premise";
            } else if (activeDataset === "onPrem") {
                url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
                payload.module = "on_premise";
            } else if (activeDataset === "cloud") {
                url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
                payload.module = "cloud";
            } else {
                url = END_POINTS.CYBERRESILIENCE_DASHBOARD_DAILY;
            }
            const response = await axiosInstance.post(url, payload, { signal: (controller as any).signal });
            const formattedData = {
                date: dayjs(date).format("MM/DD/YYYY"),
                data: response.data.data.event_data.map((event: any) => ({
                    time: formatTimeAgo(event.event_date),
                    security_score: parseFloat(event.current_score).toFixed(2),
                    score_change: parseFloat(event.score_difference).toFixed(2),
                    event_type: event.event_name,
                    event_code: event.event_code,
                    referenceID: event.reference_id,
                    originalEvent: event, // Keep the original event data for details
                })),
            };
            setEventListData(formattedData);
            setEventListOpen(true);
        } catch (error) {
            console.error("Failed to fetch event data:", error);
        } finally {
            // Clear the abort ref after this request completes/aborts
            tooltipAbortRef.current = null;
        }
    };

    const customTooltip = useCallback(
        function ({ seriesIndex, dataPointIndex, w }: any) {
            // Get data from trendsData arrays using dataPointIndex
            const score = trendsData.postureScores[dataPointIndex] ?? 0;
            const hasNewEvent = trendsData.newEvents[dataPointIndex] === true;
            const timestamp = trendsData.timestamps[dataPointIndex];

            // Base tooltip for all cases
            const baseTooltip = `
            <div class="apexcharts-tooltip-box" style="padding: 12px; font-family: inherit; max-width: 400px; background: ${isDarkMode ? "#1a1a2e" : "#fff"}; border-radius: 8px; color: ${isDarkMode ? "#fff" : "#000"};">
              <div style="margin-bottom: 12px; font-weight: bold; color: ${isDarkMode ? "#fff" : "#000"
                }; font-size: 14px;">
                Security Score on ${dayjs(timestamp).format("MM/DD/YYYY")}
              </div>
              <div style="display: flex; align-items: center; margin-bottom: 8px">
                <span style="display: inline-block; width: 8px; height: 8px; background: #FF6B00; border-radius: 50%; margin-right: 8px;"></span>
                <span style="color: ${isDarkMode ? "#fff" : "#000"};">Security Rating: ${Math.round(score)}</span>
              </div>
          `;

            // If no scan for this day, show "No events today"
            if (!hasNewEvent && timestamp) {
                return (
                    baseTooltip +
                    `
              <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                    }; color: ${isDarkMode ? "#b3b3b3" : "#666"}; font-style: italic;">
                <span>No events </span>
              </div>
            </div>
            `
                );
            }

            if (hasNewEvent && timestamp) {
                const dateStr = dayjs(timestamp).format("YYYY-MM-DD");

                // Check if we need to fetch new data for this specific date
                if (
                    currentTooltipDateRef.current !== dateStr ||
                    !tooltipDataRef.current
                ) {
                    memoizedFetchTooltipEventData(dateStr);
                }

                // Show event data in tooltip if we have data for this date
                if (
                    tooltipDataRef.current &&
                    currentTooltipDateRef.current === dateStr
                ) {
                    const eventsHtml = tooltipDataRef.current.data
                        .map((event: any) => {
                            const getEventType = (eventCode: string, eventName: string) => {
                                if (eventCode === "new_scan") return "New Scan";
                                if (eventCode === "rescan") return "Re Scan";
                                if (eventName.includes("Risk Accepted")) return "Risk Accepted";
                                if (eventName.includes("Risk Unaccepted"))
                                    return "Risk Unaccepted";
                                if (eventName.includes("Device Decommissioned"))
                                    return "Device Decommissioned";
                                if (eventName.includes("Device Deleted"))
                                    return "Device Deleted";
                                if (eventName.includes("Master file Update"))
                                    return "Master file Update";
                                if (eventName.includes("Asset Critacality"))
                                    return "Asset Critacality";
                                return eventName.split("-")[0].trim();
                            };

                            const eventType = getEventType(
                                event.event_code,
                                event.event_type
                            );
                            const scoreChange = event.score_change;
                            const hasScore = scoreChange !== 0;

                            return `
                <div style="display: flex; align-items: center; gap: 8px; padding: 4px 0; border-bottom: 1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                                };">
                  <span style="color: ${isDarkMode ? "#b3b3b3" : "#666"
                                }; font-size: 12px; min-width: 60px;">${event.time}</span>
                  ${hasScore
                                    ? `<span style="color: ${isDarkMode ? "#fff" : "#000"
                                    }; font-size: 12px; font-weight: 500;">- ${event.security_score
                                    }</span>`
                                    : ""
                                }
                  ${hasScore
                                    ? `
                    <span style="display: flex; align-items: center; gap: 4px; min-width: 30px;">
                      <span style="color: ${scoreChange > 0 ? "#4caf50" : "#f44336"
                                    }; font-size: 14px;">${scoreChange > 0 ? "▲" : "▼"}</span>
                      <span style="color: ${scoreChange > 0 ? "#4caf50" : "#f44336"
                                    }; font-size: 12px; font-weight: 700;">${parseFloat(
                                        scoreChange
                                    ).toFixed(2)}</span>
                    </span>
                  `
                                    : ""
                                }
                  <span style="color: ${isDarkMode ? "#fff" : "#000"
                                }; font-size: 12px; font-weight: 500;">(${eventType})</span>
                </div>
              `;
                        })
                        .join("");

                    return (
                        baseTooltip +
                        `
              <div style="max-height: 200px; overflow-y: auto;">
                ${eventsHtml}
              </div>
            </div>
            `
                    );
                } else {
                    // Show loading state while fetching data
                    return (
                        baseTooltip +
                        `
              <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                        }">
                <span style="display: inline-block; width: 8px; height: 8px; background: #FF0000; border-radius: 50%; margin-right: 8px;"></span>
                <span style="color: #FF0000;">Loading events...</span>
              </div>
            </div>
            `
                    );
                }
            }
        },
        [isDarkMode, memoizedFetchTooltipEventData, trendsData]
    );

    // Helper function to check if event is clickable
    const isEventClickable = (event: any) => {
        return !(
            event.event_code === "asset_recom" ||
            event.event_code === "device_decom" ||
            event.event_code === "asset_decom" ||
            event.event_code === "device_recom"
        );
    };

    const handleEventItemClick = (event: any) => {
        if (event.new_event === false) return;
        if (!isEventClickable(event)) {
            return;
        }

        // Validate that we have the required data
        if (!event.originalEvent) {
            return;
        }

        // Fix field name mapping - use reference_id as referenceID
        const eventWithCorrectFields = {
            ...event.originalEvent,
            referenceID:
                event.originalEvent.reference_id || event.originalEvent.referenceID,
        };

        setSelectedEvent(eventWithCorrectFields);
        setEventClicked(true);
        setEventListOpen(false);
    };

    const chartOptions: ApexOptions = useMemo(
        () => {
            // Generate discrete markers for data points (like TrendsCard)
            const discreteMarkers: Array<{
                seriesIndex: number;
                fillColor: string;
                strokeColor: string;
                size: number;
            }> = [];

            // Show blue dots on posture score line for each event point
            trendsData.newEvents.forEach((isEvent) => {
                if (isEvent) {
                    // Add blue marker for Posture Score line (series 0)
                    discreteMarkers.push({
                        seriesIndex: 0,
                        fillColor: '#3A2ADA', // Blue color to match posture score line
                        strokeColor: '#fff',
                        size: 6,
                    });
                }
            });

            // Calculate intervals based on data span and time range
            let weeklyIntervals: number[] = [];
            let monthlyIntervals: number[] = [];
            let dataSpanMonths = 0;

            // Calculate the expected date range based on timeRange selection
            let rangeStartDate: dayjs.Dayjs;
            let rangeEndDate = dayjs(); // Today
            let rangeSpanMonths = 0;

            if (timeRange === 'custom' && appliedFromDate && appliedToDate) {
                rangeStartDate = appliedFromDate.startOf('day');
                rangeEndDate = appliedToDate.startOf('day');
            } else {
                const days = getDaysFromTimeRange(timeRange);
                rangeStartDate = dayjs().subtract(days, 'days').startOf('day');
            }

            // Calculate the time range span in months
            rangeSpanMonths = rangeEndDate.diff(rangeStartDate, 'month', true);

            if (trendsData.timestamps.length > 0) {
                const firstTimestamp = dayjs(trendsData.timestamps[0]).valueOf();
                const lastTimestamp = dayjs(trendsData.timestamps[trendsData.timestamps.length - 1]).valueOf();
                const firstDate = dayjs(firstTimestamp);
                const lastDate = dayjs(lastTimestamp);

                // Calculate data span in months
                dataSpanMonths = lastDate.diff(firstDate, 'month', true);

                // If the selected time range spans more than 2 months, generate monthly intervals from the range start
                if (rangeSpanMonths > 2) {
                    // Generate monthly intervals from the start of the selected range to the end
                    let currentDate = rangeStartDate.startOf('month');
                    while (!currentDate.isAfter(rangeEndDate, 'month')) {
                        monthlyIntervals.push(currentDate.valueOf());
                        currentDate = currentDate.add(1, 'month');
                    }
                } else if (timeRange === '30days') {
                    // For 1 month view, generate weekly intervals (every 7 days) from first data point
                    let currentDate = firstDate.startOf('day');
                    while (currentDate.isBefore(lastDate) || currentDate.isSame(lastDate, 'day')) {
                        weeklyIntervals.push(currentDate.valueOf());
                        currentDate = currentDate.add(7, 'days');
                    }
                }
            } else if (rangeSpanMonths > 2) {
                // Even if no data, generate monthly intervals for the selected range
                let currentDate = rangeStartDate.startOf('month');
                while (!currentDate.isAfter(rangeEndDate, 'month')) {
                    monthlyIntervals.push(currentDate.valueOf());
                    currentDate = currentDate.add(1, 'month');
                }
            }

            return {
                chart: {
                    height: height ?? (compact ? 10 : 140),
                    zoom: { enabled: false },
                    toolbar: { show: false },
                    parentHeightOffset: 0,
                    offsetX: 0,
                    offsetY: 0,
                },
                events: {
                    click: (
                        event: any,

                        config: { dataPointIndex: number }
                    ) => {
                        const { dataPointIndex } = config;
                        if (dataPointIndex >= 0) {
                            // Check if this data point has scan data
                            const hasNewEvent = trendsData.newEvents[dataPointIndex] === true;

                            // If no scan, don't open drawer but tooltip will still show
                            if (!hasNewEvent) return;

                            const dataPoint = getMainChartData()[dataPointIndex];
                            if (dataPoint?.new_event) {
                                const date = dayjs(dataPoint.date_time).format("YYYY-MM-DD");
                                handleEventClick(date);
                            }
                        }
                    },
                },
                stroke: {
                    curve: 'smooth',
                    width: 1,
                },
                colors: ['#3A2ADA', '#B8A3FF'],
                dataLabels: {
                    enabled: false,
                },
                markers: {
                    size: 0, // Default no markers
                    discrete: discreteMarkers, // Only show blue markers for event points
                    hover: {
                        sizeOffset: 2,
                    },
                },
                tooltip: {
                    custom: customTooltip,
                },
                xaxis: {
                    categories: trendsData.hours,
                    title: {
                        text: 'Time Stamp',
                        style: {
                            fontSize: '12px',
                            fontWeight: 700,
                            color: isDarkMode ? '#FFFFFF' : 'black',
                        },
                    },
                    labels: {
                        style: {
                            colors: isDarkMode ? '#FFFFFF' : 'black',
                            fontSize: '11px',
                        },
                        rotate: -45,
                        rotateAlways: true,
                        hideOverlappingLabels: false,
                        showDuplicates: false,
                        offsetX: 0,
                        offsetY: 0,
                        formatter: (value: string, timestamp?: number) => {
                            // For 30 days view, just return the category value directly (already processed in hours array)
                            if (timeRange === '30days') {
                                return value;
                            }

                            if (!timestamp) return value;

                            // If the selected time range spans more than 2 months, show monthly intervals only
                            if (rangeSpanMonths > 2 && monthlyIntervals.length > 0) {
                                const isMonthlyInterval = monthlyIntervals.some(monthTimestamp => {
                                    const diff = Math.abs(timestamp - monthTimestamp);
                                    const diffDays = diff / (1000 * 60 * 60 * 24);
                                    // Allow within 5 days of the first day of month
                                    return diffDays <= 5;
                                });

                                if (isMonthlyInterval) {
                                    return dayjs(timestamp).format("MMM 'YY");
                                }
                                return "";
                            }

                            // For other ranges, use default formatting
                            return dayjs(timestamp).format("MMM 'YY");
                        },
                        datetimeFormatter: {
                            year: "yyyy",
                            month: "MMM 'YY",
                            day: "DD MMM",
                            hour: "HH:mm",
                        },
                    },
                    tickAmount: timeRange === '30days' ? undefined : undefined,
                    axisBorder: {
                        color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                    },
                    axisTicks: {
                        color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                    },
                },
                yaxis: [
                    {
                        title: {
                            text: 'Posture score',
                            style: {
                                fontSize: '12px',
                                fontWeight: 700,
                                color: isDarkMode ? '#FFFFFF' : 'black',
                            },
                        },
                        labels: {
                            style: {
                                colors: isDarkMode ? '#FFFFFF' : 'black',
                                fontSize: '12px',
                                fontWeight: 500,
                            },
                            formatter: (val: number) => `${Math.round(val)}`,
                        },
                        min: 0,
                        max: maxPostureScore,
                    },
                    {
                        opposite: true,
                        title: {
                            text: 'No. of Changes',
                            style: {
                                fontSize: '12px',
                                fontWeight: 700,
                                color: isDarkMode ? '#FFFFFF' : 'black',
                            },
                        },
                        labels: {
                            style: {
                                colors: isDarkMode ? '#FFFFFF' : 'black',
                                fontSize: '12px',
                                fontWeight: 700,
                            },
                            formatter: (val: number) => `${Math.round(val)}`,
                        },
                        min: 0,
                        max: maxNumChanges,
                    },
                ],
                legend: {
                    show: false,
                },
                grid: {
                    borderColor: isDarkMode ? '#FFFFFF' : '#e7e7e7',
                    padding: {
                        left: 5,
                        right: 5,
                        top: 0,
                        bottom: 0,
                    },
                },
                plotOptions: {
                    bar: {
                        columnWidth: '80%',
                    },
                },
            };
        },
        [trendsData.hours, trendsData.newEvents, trendsData.timestamps, theme, height, compact, maxPostureScore, maxNumChanges, isDarkMode, customTooltip, timeRange, appliedFromDate, appliedToDate]
    );

    const chartSeries = [
        {
            name: 'Posture Score',
            type: 'line',
            data: trendsData.postureScores,
        },
        {
            name: 'Number of changes',
            type: 'column',
            data: trendsData.numberOfChanges,
        },
    ];

    const hasNoTrendData = !isChartLoading && trendsData.hours.length === 0;

    return (
        <Accordion
            expanded={isExpanded}
            onChange={(_event, expanded) => setIsExpanded(expanded)}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#ffffff',
                boxShadow: 'none',
                margin: 0,
                '&:before': {
                    display: 'none',
                },
                mb: 2,
            }}
            disableGutters
            elevation={0}
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon
                        sx={{
                            color: theme.palette.text.primary,
                            transition: 'transform 0.3s',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                }
                sx={{
                    flexDirection: 'row-reverse',
                    borderBottom: isExpanded ? `1px solid ${theme.palette.divider}` : 'none',
                    minHeight: '56px',
                    '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        width: '100%',
                    },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                            fontSize: '14px',
                            color: theme.palette.text.primary,
                        }}
                    >
                        Trends
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: '#3A2ADA',
                                    }}
                                />
                                <span style={{ fontSize: '13px', fontWeight: 500, color: theme.palette.text.secondary }}>
                                    Posture Score: {trendsData.latestPostureScore.toString().padStart(2, '0')}
                                </span>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: '#8787AE',
                                    }}
                                />
                                <span style={{ fontSize: '13px', fontWeight: 500, color: theme.palette.text.secondary }}>
                                    No. of Changes: {trendsData.latestNumChanges.toString().padStart(2, '0')}
                                </span>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }} onClick={(e) => e.stopPropagation()}>
                                <Select
                                    value={timeRange}
                                    open={selectOpen}
                                    onClose={() => setSelectOpen(false)}
                                    onOpen={() => setSelectOpen(true)}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        const newValue = e.target.value;
                                        // Always reset dates when switching between menu items
                                        setFromDate(null);
                                        setToDate(null);
                                        setDateRangeError('');
                                        setTimeRange(newValue);
                                        setCustomDays(null); // Reset custom days when selecting predefined range
                                        setAppliedFromDate(null); // Reset applied dates when selecting predefined range
                                        setAppliedToDate(null); // Reset applied dates when selecting predefined range
                                        setSelectOpen(false); // Close the Select dropdown
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    renderValue={(value) => getTimeRangeDisplayText(value)}
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 800,
                                        height: '32px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.divider,
                                        },
                                    }}
                                >
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Stack spacing={2} padding={1}>
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                <DatePicker
                                                    label="From"
                                                    value={fromDate}
                                                    onChange={(newValue) => {
                                                        setFromDate(newValue);
                                                        if (newValue && toDate) {
                                                            const error = validateDateRange(newValue, toDate);
                                                            setDateRangeError(error);
                                                        } else {
                                                            setDateRangeError('');
                                                        }
                                                    }}
                                                    maxDate={dayjs()}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '12px',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                <DatePicker
                                                    label="To"
                                                    value={toDate}
                                                    onChange={(newValue) => {
                                                        setToDate(newValue);
                                                        if (newValue) {
                                                            const error = validateDateRange(fromDate, newValue);
                                                            setDateRangeError(error);
                                                        } else {
                                                            setDateRangeError('');
                                                        }
                                                    }}
                                                    maxDate={dayjs()}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '12px',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleApplyCustomDateRange}
                                                    disabled={!fromDate || !toDate || !!dateRangeError}
                                                    sx={{
                                                        height: '40px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Apply
                                                </Button>
                                            </Box>
                                            {dateRangeError ? (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: theme.palette.error.main,
                                                        fontSize: '12px',
                                                        fontStyle: "italic"
                                                    }}
                                                >
                                                    {dateRangeError}
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <InfoIcon sx={{
                                                        fontSize: 18,
                                                        color: theme.palette.mode === "dark" ? "white" : theme.palette.primary.main
                                                    }} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            fontStyle: "italic"
                                                        }}
                                                    >
                                                        You can select a minimum period of 7 days
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </LocalizationProvider>
                                    <MenuItem value="7days">Last 7 Days</MenuItem>
                                    <MenuItem value="30days">Last 1 Month</MenuItem>
                                    <MenuItem value="90days">Last 3 Months</MenuItem>
                                    <MenuItem value="180days">Last 6 Months</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, py: 0 }}>
                <Box sx={{ px: 0, py: 0, position: 'relative' }}>
                    {isChartLoading ? (
                        <Box sx={{
                            px: 3,
                            py: 2,
                            height: height ?? (compact ? 100 : 140),
                            minHeight: height ?? (compact ? 100 : 140),
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}>
                            {/* Y-axis labels skeleton */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={80} height={16} />
                            </Box>
                            {/* Chart area skeleton with bars and line points */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'space-around',
                                flex: 1,
                                gap: 1,
                                position: 'relative',
                                pb: 3,
                            }}>
                                {Array.from({ length: 7 }).map((_, index) => {
                                    const barHeight = [45, 35, 55, 40, 50, 38, 42][index] || 40;
                                    return (
                                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1, position: 'relative' }}>
                                            {/* Bar skeleton */}
                                            <Skeleton
                                                variant="rectangular"
                                                width="100%"
                                                height={`${barHeight}%`}
                                                sx={{ borderRadius: '4px 4px 0 0', minHeight: 20 }}
                                            />
                                            {/* Line point skeleton */}
                                            <Skeleton
                                                variant="circular"
                                                width={8}
                                                height={8}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${100 - barHeight - 5}%`,
                                                }}
                                            />
                                            {/* X-axis label skeleton */}
                                            <Skeleton variant="text" width={40} height={12} sx={{ mt: 'auto' }} />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : hasNoTrendData ? (
                        <Box
                            sx={{
                                px: 3,
                                py: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                height: height ?? (compact ? 100 : 140),
                                minHeight: height ?? (compact ? 100 : 140),
                            }}
                        >
                            No data available
                        </Box>
                    ) : (
                        <ReactApexChart
                            options={chartOptions}
                            series={chartSeries as any}
                            type="line"
                            height={height ?? (compact ? 100 : 140)}
                        />
                    )}
                </Box>
            </AccordionDetails>

            <Drawer
                anchor="right"
                open={eventClicked}
                onClose={() => setEventClicked(false)}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: { xs: "100%", sm: "50%" },
                        bgcolor: isDarkMode ? "background.paper" : "white",
                        boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
                    },
                }}
            >
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton
                                onClick={() => {
                                    setEventClicked(false);
                                    setEventListOpen(true);
                                }}
                                size="small"
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <EventIcon color="primary" />
                            <Typography variant="h6">Event Details</Typography>
                            {(selectedEvent?.event_code === "new_scan" ||
                                selectedEvent?.event_code === "rescan" ||
                                selectedEvent?.event_code === "cloud") && (
                                    <img
                                        src={Layer123}
                                        alt="Layer123"
                                        style={{ height: 22, cursor: "pointer" }}
                                        onClick={() => {
                                            if (
                                                selectedEvent?.event_code === "new_scan" ||
                                                selectedEvent?.event_code === "rescan"
                                            ) {
                                                if (selectedEvent?.referenceID) {
                                                    navigate(`/onprem/scan/${selectedEvent.referenceID}`);
                                                }
                                            } else if (selectedEvent?.event_code === "cloud") {
                                                navigate(`/cloud/scan/${selectedEvent.referenceID}`);
                                            }
                                        }}
                                    />
                                )}
                        </Box>
                        <IconButton
                            onClick={() => {
                                setEventClicked(false);
                                setEventListOpen(false);
                            }}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Event Content */}
                    <Box
                        sx={{
                            p: 0,
                            flexGrow: 1,
                            overflowY: "auto",
                        }}
                    >
                        <CyberResilienceRediretions event={selectedEvent} />
                    </Box>
                </Box>
            </Drawer>

            {/* Event List Drawer */}
            <Drawer
                anchor="right"
                open={eventListOpen}
                onClose={() => setEventListOpen(false)}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: { xs: "100%", sm: "40%" },
                        bgcolor: isDarkMode ? "background.paper" : "white",
                        boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
                    },
                }}
            >
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton onClick={() => setEventListOpen(false)} size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <EventIcon color="primary" />
                            <Typography variant="h6">
                                Events on {eventListData?.date}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setEventListOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Event List Content */}
                    <Box
                        sx={{
                            p: 2,
                            flexGrow: 1,
                            overflowY: "auto",
                        }}
                    >
                        <style>
                            {`
                .event-list-scroll::-webkit-scrollbar {
                  width: 8px;
                }
                .event-list-scroll::-webkit-scrollbar-track {
                  background: ${isDarkMode
                                    ? "rgba(255, 255, 255, 0.05)"
                                    : "rgba(0, 0, 0, 0.05)"
                                };
                  border-radius: 4px;
                }
                .event-list-scroll::-webkit-scrollbar-thumb {
                  background: ${isDarkMode
                                    ? "rgba(99, 102, 241, 0.3)"
                                    : "rgba(99, 102, 241, 0.2)"
                                };
                  border-radius: 4px;
                }
                .event-list-scroll::-webkit-scrollbar-thumb:hover {
                  background: ${isDarkMode
                                    ? "rgba(99, 102, 241, 0.5)"
                                    : "rgba(99, 102, 241, 0.4)"
                                };
                }
              `}
                        </style>
                        <Box
                            className="event-list-scroll"
                            sx={{ height: "100%", overflowY: "auto" }}
                        >
                            {eventListData?.data?.map((event: any, index: number) => {

                                const getEventType = (eventCode: string, eventName: string) => {
                                    if (!eventCode || !eventName) return "Event";
                                    if (eventCode === "new_scan") return "New Scan";
                                    if (eventCode === "rescan") return "Re Scan";
                                    if (eventName.includes("Risk Accepted"))
                                        return "Risk Accepted";
                                    if (eventName.includes("Risk Unaccepted"))
                                        return "Risk Unaccepted";
                                    if (eventName.includes("Device Decommissioned"))
                                        return "Device Decommissioned";
                                    if (eventName.includes("Device Deleted"))
                                        return "Device Deleted";
                                    if (eventName.includes("Master file Update"))
                                        return "Master file Update";
                                    if (eventName.includes("Asset Critacality"))
                                        return "Asset Critacality";
                                    return eventName.split("-")[0].trim();
                                };

                                const eventType = getEventType(
                                    event.event_code,
                                    event.event_name || event.event_type
                                );
                                const scoreChange = event.score_change;
                                const hasScore = scoreChange !== 0;

                                // Get event icon based on event type
                                const getEventIcon = (eventCode: string, eventName: string) => {
                                    if (!eventCode || !eventName) return <EventIcon />;
                                    if (eventCode === "new_scan" || eventCode === "rescan")
                                        return <SecurityIcon />;
                                    if (eventName.includes("Risk")) return <TrendingUpIcon />;
                                    if (eventName.includes("Device")) return <TimelineIcon />;
                                    return <EventIcon />;
                                };

                                // Get event color based on event type
                                const getEventColor = (
                                    eventCode: string,
                                    eventName: string
                                ) => {
                                    if (!eventCode || !eventName) return "#6366f1";
                                    if (eventCode === "new_scan" || eventCode === "rescan")
                                        return "#10b981";
                                    if (eventName.includes("Risk Accepted")) return "#f59e0b";
                                    if (eventName.includes("Risk Unaccepted")) return "#ef4444";
                                    if (eventName.includes("Device")) return "#8b5cf6";
                                    return "#6366f1";
                                };

                                const eventIcon = getEventIcon(
                                    event.event_code,
                                    event.event_name || event.event_type
                                );
                                const eventColor = getEventColor(
                                    event.event_code,
                                    event.event_name || event.event_type
                                );

                                return (
                                    <Card
                                        key={index}
                                        onClick={isEventClickable(event) ? () => handleEventItemClick(event) : undefined}
                                        sx={{
                                            mb: 2,
                                            cursor: isEventClickable(event) ? "pointer" : "default",
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        <CardActionArea
                                            sx={{
                                                cursor: isEventClickable(event) ? "pointer" : "default",
                                            }}
                                        >
                                            <Box sx={{ p: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                p: 0.5,
                                                                borderRadius: 1,
                                                                bgcolor: `${eventColor}15`,
                                                            }}
                                                        >
                                                            {React.cloneElement(eventIcon, {
                                                                sx: {
                                                                    color: eventColor,
                                                                    fontSize: 18,
                                                                },
                                                            })}
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {event.time}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                        sx={{ color: eventColor }}
                                                    >
                                                        {eventType}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                                                >
                                                    {hasScore && (
                                                        <>
                                                            <Typography variant="body2">
                                                                Score: {event.security_score}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 0.5,
                                                                }}
                                                            >
                                                                {scoreChange > 0 ? (
                                                                    <TrendingUpIcon
                                                                        sx={{ color: "success.main", fontSize: 16 }}
                                                                    />
                                                                ) : (
                                                                    <TrendingDownIcon
                                                                        sx={{ color: "error.main", fontSize: 16 }}
                                                                    />
                                                                )}
                                                                <Typography
                                                                    variant="body2"
                                                                    color={
                                                                        scoreChange > 0
                                                                            ? "success.main"
                                                                            : "error.main"
                                                                    }
                                                                    fontWeight="medium"
                                                                >
                                                                    {scoreChange > 0 ? "+" : ""}
                                                                    {scoreChange}
                                                                </Typography>
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </Drawer>
        </Accordion>
    );
};

export default EventsTrendsCard;
